import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Users, Gamepad2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import orbitlogo from "../assets/orbit_logo.png";
import how_ai_works_bg from "../assets/howaiworks_bg.png";
import how_ai_works_bg_mobile from "../assets/how_works_mobile.png";

import howaiworks_bg_eng from "../assets/howaiworks_bg_eng.png";
import howaiworks_bg_eng_mobile from "../assets/howaiworks_bg_eng_mobile.png";
import i18n from "../i18n";
/**
 * ANIMATIONS
 */
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(255, 87, 34, 0.4), inset 0 0 10px rgba(255, 87, 34, 0.2); }
  50% { box-shadow: 0 0 50px rgba(255, 87, 34, 0.8), inset 0 0 25px rgba(255, 87, 34, 0.5); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) translateX(0px); }
  33% { transform: translateY(-10px) translateX(5px); }
  66% { transform: translateY(5px) translateX(-5px); }
`;

const particleFloat = keyframes`
  0% { transform: translate(0, 0) scale(1); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translate(var(--tx), var(--ty)) scale(0); opacity: 0; }
`;

const orbitalSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const flowDash = keyframes`
  to { stroke-dashoffset: -40; }
`;

const AnimatedPath1 = styled.path`
  animation: ${flowDash} 1s linear infinite;
`;

const AnimatedPath2 = styled.path`
  animation: ${flowDash} 1s linear infinite reverse;
`;

/**
 * LAYOUT CONTAINERS
 */
const Section = styled.section`
  background-color: #000;
  color: #fff;
  padding: 4rem 2rem;
  overflow: hidden;
  font-family: "Inter", sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  img {
    scale: 1;
    margin-left: 0;
    margin-top: 0;
  }

  @media (max-width: 768px) {
    width: 100vw;
    height: auto;
    scale: 1.25;
    margin-left: -5vw;
    margin-top: -10vh;
  }
`;

const HeaderContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 3rem;
  z-index: 10;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 800;
  margin: 0;
  color: #ff8a50;
  text-align: center;
  background: linear-gradient(90deg, #ff8a50 0%, #ff5722 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const LogoContainer = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #fff;

  img {
    width: 140px;
  }
`;

const DiagramWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 1200px;
  min-height: 700px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 1024px) {
    min-height: 1000px;
    flex-direction: column;
    gap: 4rem;
  }
`;

/**
 * SVG CONNECTION LINES
 */
const SvgPathsLayer = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;

  @media (max-width: 1024px) {
    display: none; // Hide complex SVG lines on mobile, rely on flex column
  }
`;

/**
 * INDIVIDUAL NODES
 */
const NodePosition = styled.div<{
  $t?: string;
  $b?: string;
  $l?: string;
  $r?: string;
}>`
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: 1025px) {
    position: absolute;
    top: ${(props) => props.$t || "auto"};
    bottom: ${(props) => props.$b || "auto"};
    left: ${(props) => props.$l || "auto"};
    right: ${(props) => props.$r || "auto"};
  }
`;

const NodeTitle = styled.h4`
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  margin-top: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
  white-space: nowrap;
`;

/**
 * 1. DISPERSOS (Scattered Particles)
 */
const DispersosContainer = styled.div`
  width: 150px;
  height: 150px;
  position: relative;
`;

const ParticleDot = styled.div<{ $delay: string; $tx: string; $ty: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 6px;
  height: 6px;
  background: #ff8a50;
  border-radius: 50%;
  box-shadow: 0 0 10px #ff8a50;
  --tx: ${(props) => props.$tx};
  --ty: ${(props) => props.$ty};
  animation: ${particleFloat} 3s ease-out infinite;
  animation-delay: ${(props) => props.$delay};
`;

/**
 * 2. ANÁLISE COMPORTAMENTAL (Converging icons) & 4. CLUSTERIZA (Network)
 */
const CircleIconGroup = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 2px solid rgba(255, 138, 80, 0.4);
  background: radial-gradient(
    circle,
    rgba(255, 87, 34, 0.15) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  box-shadow: 0 0 30px rgba(255, 87, 34, 0.2);
  animation: ${float} 6s ease-in-out infinite;

  svg {
    color: #ff8a50;
    width: 40px;
    height: 40px;
    filter: drop-shadow(0 0 5px rgba(255, 138, 80, 0.8));
  }
`;

const NetworkNodeLinesing = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 50%;
  border: 1px dashed rgba(255, 138, 80, 0.6);
  animation: ${orbitalSpin} 20s linear infinite;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: 8px;
    height: 8px;
    background: #ff8a50;
    border-radius: 50%;
    box-shadow: 0 0 10px #ff8a50;
  }
  &::before {
    top: -4px;
    left: 46px;
  }
  &::after {
    bottom: -4px;
    right: 46px;
  }
`;

/**
 * 3. ORBIT CENTRAL (The giant glowing ring)
 */
const CentralRingContainer = styled.div`
  position: relative;
  width: 350px;
  height: 350px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;

  @media (max-width: 768px) {
    width: 280px;
    height: 280px;
  }
`;

const OuterRingGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: inherit;
  border: 4px solid rgba(255, 87, 34, 0.8);
  animation: ${pulseGlow} 3s ease-in-out infinite;
  background: radial-gradient(
    circle,
    rgba(0, 0, 0, 1) 50%,
    rgba(255, 87, 34, 0.15) 100%
  );
`;

const OrbitText = styled.h3`
  font-size: 4.5rem;
  font-weight: 800;
  margin: 0;
  color: #ff8a50;
  background: linear-gradient(180deg, #ffffff 0%, #ff8a50 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 0 15px rgba(255, 87, 34, 0.6));
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 3.5rem;
  }
`;

/**
 * 5. RECOMENDA (Game Grid)
 */
const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-top: 1rem;
`;

const GameCard = styled.div<{ $bg: string }>`
  width: 55px;
  height: 55px;
  border-radius: 12px;
  background: ${(props) => props.$bg};
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 4px 10px rgba(0, 0, 0, 0.5),
    inset 0 0 10px rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
    border-color: rgba(255, 87, 34, 0.5);
  }

  svg {
    width: 24px;
    height: 24px;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const GameLabel = styled.span`
  font-size: 0.5rem;
  color: #aaa;
  position: absolute;
  bottom: -15px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s;

  ${GameCard}:hover & {
    opacity: 1;
  }
`;

/**
 * COMPONENT
 */
export const HowAIWorks: React.FC = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  const language = i18n.language;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Generate random particles for "Jogadores dispersos"
  const [particles, setParticles] = useState<
    Array<{ id: number; tx: string; ty: string; delay: string }>
  >([]);

  useEffect(() => {
    const newParticles = Array.from({ length: 40 }).map((_, i) => ({
      id: i,
      tx: `${(Math.random() - 0.5) * 200}px`,
      ty: `${(Math.random() - 0.5) * 200}px`,
      delay: `${Math.random() * 3}s`,
    }));
    setParticles(newParticles);
  }, []);

  const gameColors = [
    "linear-gradient(135deg, #FF6B6B 0%, #C0392B 100%)", // Red / Tiger
    "linear-gradient(135deg, #FF9FF3 0%, #8E44AD 100%)", // Pink / Sweet Bonanza
    "linear-gradient(135deg, #54A0FF 0%, #2980B9 100%)", // Blue / Roulette
    "linear-gradient(135deg, #F1C40F 0%, #F39C12 100%)", // Gold / Dragon
    "linear-gradient(135deg, #1DD1A1 0%, #16A085 100%)", // Green
    "linear-gradient(135deg, #FECA57 0%, #D35400 100%)", // Orange
    "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)", // Purple
    "linear-gradient(135deg, #bdc3c7 0%, #7f8c8d 100%)", // Silver
  ];

  return (
    <Section>
      {/* <HeaderContainer>
        <Title>{t("how_ai_works.title")}</Title>
      </HeaderContainer> */}

      <img
        src={
          language === "en"
            ? isMobile
              ? howaiworks_bg_eng_mobile
              : howaiworks_bg_eng
            : isMobile
              ? how_ai_works_bg_mobile
              : how_ai_works_bg
        }
        alt=""
      />

      {/* <DiagramWrapper>
        <SvgPathsLayer xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="flowGlow" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ff5722" stopOpacity="0.2" />
              <stop offset="50%" stopColor="#ff8a50" stopOpacity="1" />
              <stop offset="100%" stopColor="#ff5722" stopOpacity="0.2" />
            </linearGradient>
            <filter id="glowBlur" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>

          <g filter="url(#glowBlur)">
            <AnimatedPath1
              d="M 220 530 Q 250 350 340 250"
              fill="none"
              stroke="url(#flowGlow)"
              strokeWidth="4"
              strokeDasharray="10 10"
            />

            <path
              d="M 400 230 Q 450 250 500 300"
              fill="none"
              stroke="url(#flowGlow)"
              strokeWidth="3"
            />
            <polygon
              points="495,295 505,305 490,305"
              fill="#ff8a50"
              transform="rotate(-45 500 300)"
            />

            <path
              d="M 700 300 Q 750 250 800 230"
              fill="none"
              stroke="url(#flowGlow)"
              strokeWidth="3"
            />
            <polygon
              points="795,225 805,235 790,235"
              fill="#ff8a50"
              transform="rotate(45 800 230)"
            />

            <AnimatedPath2
              d="M 860 250 Q 950 350 980 500"
              fill="none"
              stroke="url(#flowGlow)"
              strokeWidth="4"
              strokeDasharray="15 15"
            />
          </g>
        </SvgPathsLayer>

        <NodePosition $b="15%" $l="10%">
          <DispersosContainer>
            {particles.map((p) => (
              <ParticleDot key={p.id} $delay={p.delay} $tx={p.tx} $ty={p.ty} />
            ))}
          </DispersosContainer>
          <NodeTitle>Jogadores dispersos</NodeTitle>
        </NodePosition>

        <NodePosition $t="20%" $l="28%">
          <NodeTitle style={{ marginBottom: "1rem", marginTop: 0 }}>
            Análise Comportamental
          </NodeTitle>
          <CircleIconGroup>
            <Users />
          </CircleIconGroup>
        </NodePosition>

        <NodePosition
          $t="17.5%"
          $l="35.5%"
          style={{
            transform: "none",
            position: "relative",
            left: "auto",
            top: "auto",
          }}
        >
          <CentralRingContainer>
            <OuterRingGlow />
            <OrbitText>Orbit</OrbitText>
          </CentralRingContainer>
        </NodePosition>

        <NodePosition $t="20%" $r="28%">
          <CircleIconGroup>
            <NetworkNodeLinesing />
            <Users style={{ width: 30, height: 30 }} />
          </CircleIconGroup>
          <NodeTitle>Clusteriza</NodeTitle>
        </NodePosition>

        <NodePosition $b="10%" $r="10%">
          <NodeTitle style={{ marginBottom: "1rem", marginTop: 0 }}>
            Recomenda
          </NodeTitle>
          <GamesGrid>
            {gameColors.map((bg, i) => (
              <div
                key={i}
                style={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <GameCard $bg={bg}>
                  <Gamepad2 />
                </GameCard>
                <GameLabel>Game {i + 1}</GameLabel>
              </div>
            ))}
          </GamesGrid>
        </NodePosition>
      </DiagramWrapper> */}
    </Section>
  );
};

export default HowAIWorks;

